import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {IFleetScore, IMachineryContext, ITrendBucket, ScorePeriod} from "../../../models/IDrivingScore";
import {fetchFleetScore, fetchFleetTrend, fetchMachineryTrend, fetchMachineryContext} from "./scoreActions";

interface IDrivingScoreState {
    fleet: IFleetScore | null;
    period: ScorePeriod;
    isLoading: boolean;
    error: string | null;

    // тренд балла парка (главный экран)
    fleetTrend: ITrendBucket[];
    fleetTrendLoading: boolean;
    fleetTrendError: string | null;

    // drill-down: выбранная машина + её тренд (модалка)
    selectedMachineryId: string | null;
    trend: ITrendBucket[];
    trendLoading: boolean;
    trendError: string | null;

    // контекст машины (ночное+превышения) за период модалки
    context: IMachineryContext | null;
    contextLoading: boolean;
    contextError: string | null;
}

const initialState: IDrivingScoreState = {
    fleet: null,
    period: 'week',
    isLoading: false,
    error: null,

    fleetTrend: [],
    fleetTrendLoading: false,
    fleetTrendError: null,

    selectedMachineryId: null,
    trend: [],
    trendLoading: false,
    trendError: null,

    context: null,
    contextLoading: false,
    contextError: null,
};

export const DrivingScoreSlice = createSlice({
    name: "driving_score_slice",
    initialState,
    reducers: {
        setScorePeriod: (state, action: PayloadAction<ScorePeriod>) => {
            state.period = action.payload;
        },
        // открыть модалку детализации по машине
        selectMachinery: (state, action: PayloadAction<string>) => {
            state.selectedMachineryId = action.payload;
            state.trend = [];
            state.trendError = null;
            state.context = null;
            state.contextError = null;
        },
        // закрыть модалку
        closeMachinery: (state) => {
            state.selectedMachineryId = null;
            state.trend = [];
            state.trendError = null;
            state.context = null;
            state.contextError = null;
        },
        resetDrivingScore: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            // балл парка
            .addCase(fetchFleetScore.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchFleetScore.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = null;
                state.fleet = action.payload;
            })
            .addCase(fetchFleetScore.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload ?? 'Ошибка загрузки';
            })
            // тренд парка
            .addCase(fetchFleetTrend.pending, (state) => {
                state.fleetTrendLoading = true;
                state.fleetTrendError = null;
            })
            .addCase(fetchFleetTrend.fulfilled, (state, action) => {
                state.fleetTrendLoading = false;
                state.fleetTrend = action.payload;
            })
            .addCase(fetchFleetTrend.rejected, (state, action) => {
                state.fleetTrendLoading = false;
                state.fleetTrendError = action.payload ?? 'Ошибка загрузки тренда парка';
            })
            // тренд машины (модалка)
            .addCase(fetchMachineryTrend.pending, (state) => {
                state.trendLoading = true;
                state.trendError = null;
            })
            .addCase(fetchMachineryTrend.fulfilled, (state, action) => {
                state.trendLoading = false;
                // принимаем только если машина всё ещё выбрана (защита от гонок)
                if (state.selectedMachineryId === action.payload.machinery_id) {
                    state.trend = action.payload.trend;
                }
            })
            .addCase(fetchMachineryTrend.rejected, (state, action) => {
                state.trendLoading = false;
                state.trendError = action.payload ?? 'Ошибка загрузки тренда';
            })
            // контекст машины (модалка)
            .addCase(fetchMachineryContext.pending, (state) => {
                state.contextLoading = true;
                state.contextError = null;
            })
            .addCase(fetchMachineryContext.fulfilled, (state, action) => {
                state.contextLoading = false;
                // принимаем только если машина всё ещё выбрана (защита от гонок)
                if (state.selectedMachineryId === action.payload.machinery_id) {
                    state.context = action.payload.context;
                }
            })
            .addCase(fetchMachineryContext.rejected, (state, action) => {
                state.contextLoading = false;
                state.contextError = action.payload ?? 'Ошибка загрузки контекста';
            });
    },
});

export const {setScorePeriod, selectMachinery, closeMachinery, resetDrivingScore} =
    DrivingScoreSlice.actions;

export default DrivingScoreSlice.reducer;